// ===========================================
// TAP TO PAY ON IPHONE — SETUP & EDUCATION
// ===========================================
// The explicit enablement + merchant-education surface Apple requires:
// - awareness (what Tap to Pay is), reachable from the header any time
// - a clear "Set up Tap to Pay" action → the SDK's connectReader triggers
//   Apple's Terms & Conditions acceptance and "How to Tap" education
// - a configuration status / progress indicator (Not set up / Setting up /
//   Ready), so a merchant always knows where they stand
// - re-viewable education after setup

import { useEffect, useState } from 'react';
import { CreditCard, X, Check, Loader2, Smartphone, ShieldCheck, Watch } from 'lucide-react';
import ContactlessIcon from './ContactlessIcon';
import {
  isTapToPaySupported,
  getTapToPaySetupState,
  setupTapToPay,
  presentAppleTapToPayEducation,
  TapToPaySetupState,
} from '../services/tapToPay';
import { useAuthStore } from '../stores/authStore';
import { useToast } from './Toast';

const STEPS = [
  {
    icon: ShieldCheck,
    title: 'Accept Apple’s terms',
    body: 'The first time you set up, Apple asks you to accept the Tap to Pay on iPhone Terms & Conditions.',
  },
  {
    icon: Smartphone,
    title: 'Have the guest tap',
    body: 'At checkout, the guest holds their card, iPhone, or Apple Watch near the top of this iPhone.',
  },
  {
    icon: Check,
    title: 'That’s it — no hardware',
    body: 'The payment goes to your connected bank account, just like every other card payment.',
  },
];

const MANAGER_ROLES = ['owner', 'manager', 'general_manager', 'assistant_manager', 'admin'];

export default function TapToPaySetupModal({ onClose }: { onClose: () => void }) {
  const { restaurant, employee } = useAuthStore();
  // Apple 3.8: T&C accepted by an authorized party. Authorized = managers OR
  // any employee the business granted payment permissions (they're trusted
  // to take payments, so they may enable the payment method).
  const role = ((employee as any)?.primaryRole || (employee as any)?.role || '').toLowerCase();
  const perms: string[] = (employee as any)?.permissions || [];
  const isAuthorized =
    MANAGER_ROLES.includes(role) || perms.includes('*') || perms.includes('pos.payments');
  const { showError, showSuccess } = useToast();
  const [state, setState] = useState<TapToPaySetupState>('not_setup');
  const [loading, setLoading] = useState(true);
  // Apple req 4.1: education presents immediately after successful enablement
  const [showEducation, setShowEducation] = useState(false);

  const refresh = async () => {
    setState(await getTapToPaySetupState());
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const runSetup = async () => {
    try {
      await setupTapToPay({
        merchantDisplayName: restaurant?.name,
        onStatus: setState,
      });
      showSuccess('Tap to Pay on iPhone is ready.');
      await refresh();
      // Apple req 4.1/4.3: education immediately after enabling — Apple's own
      // system sheet (guideline-aligned) when available, our screens otherwise
      const shownByApple = await presentAppleTapToPayEducation();
      if (!shownByApple) setShowEducation(true);
    } catch (e: any) {
      showError(e?.message || "Couldn't finish setting up Tap to Pay.");
      await refresh();
    }
  };

  const supported = isTapToPaySupported();

  const statusChip = () => {
    if (!supported) return <span className="text-slate-500">Not available on this device</span>;
    if (state === 'ready')
      return (
        <span className="inline-flex items-center gap-1.5 text-emerald-600 font-medium">
          <Check className="w-4 h-4" /> Ready to accept cards
        </span>
      );
    if (state === 'connecting')
      return (
        <span className="inline-flex items-center gap-1.5 text-amber-600 font-medium">
          <Loader2 className="w-4 h-4 animate-spin" /> Setting up…
        </span>
      );
    return <span className="text-slate-500 font-medium">Not set up yet</span>;
  };

  // Merchant education screens (Apple reqs 4.1–4.5): how to accept
  // contactless cards AND Apple Pay / digital wallets. Shown immediately
  // after enabling, and re-openable from this modal any time.
  if (showEducation) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl max-h-[90dvh] overflow-y-auto">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <ContactlessIcon className="w-5 h-5 text-primary-600" />
              How to accept a tap
            </h3>
            <button onClick={() => setShowEducation(false)} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          <div className="p-5 space-y-5">
            {/* Contactless symbol above the top of the phone */}
            <div className="flex flex-col items-center py-2">
              <ContactlessIcon className="w-10 h-10 text-primary-600 mb-2" />
              <div className="w-24 h-36 rounded-[1.4rem] border-2 border-slate-300 relative">
                <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-8 h-1.5 rounded-full bg-slate-300" />
              </div>
              <p className="text-xs text-slate-400 mt-2">The top of this iPhone is the reader</p>
            </div>

            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                  <CreditCard className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800 text-sm">Contactless cards</p>
                  <p className="text-xs text-slate-500">
                    Ask the guest to hold their card horizontally, flat against the
                    top of this iPhone over the contactless symbol. Keep it there
                    until you see "Done" with a checkmark.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                  <Smartphone className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800 text-sm">Apple Pay &amp; digital wallets</p>
                  <p className="text-xs text-slate-500">
                    The guest double-clicks the side button of their iPhone (or
                    Apple Watch), then holds it near the top of this iPhone until
                    "Done" appears.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                  <Watch className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800 text-sm">Hold still for a moment</p>
                  <p className="text-xs text-slate-500">
                    The read takes about a second. If it doesn't take, lift away
                    and tap again — nothing is charged until you see "Done".
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowEducation(false)}
              className="w-full py-3 rounded-xl bg-primary-600 text-white font-semibold flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" /> Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl max-h-[90dvh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <ContactlessIcon className="w-5 h-5 text-primary-600" />
            Tap to Pay on iPhone
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Awareness */}
          <p className="text-slate-600 text-sm">
            Accept credit and debit cards right here — the guest taps their card,
            iPhone, or Apple Watch on the top of this iPhone. No card reader needed.
          </p>

          {/* Configuration status */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
            <span className="text-sm font-medium text-slate-700">Status</span>
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            ) : (
              <span className="text-sm">{statusChip()}</span>
            )}
          </div>

          {/* Merchant education (always visible so it can be re-read later) */}
          <div className="space-y-3">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="flex gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{s.title}</p>
                    <p className="text-xs text-slate-500">{s.body}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action */}
          {!supported ? (
            <p className="text-xs text-slate-400 text-center">
              Tap to Pay needs an iPhone XS or newer on iOS 16.4 or later.
            </p>
          ) : state === 'ready' ? (
            <div className="space-y-2">
              {/* Apple req 3.9: invite the merchant to try it out */}
              <p className="text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2 text-center font-medium">
                You're ready — try it on your next checkout: choose "Tap to Pay on iPhone" and have the guest tap.
              </p>
              {/* Apple req 4.2: education re-accessible any time */}
              <button
                onClick={async () => {
                  const shownByApple = await presentAppleTapToPayEducation();
                  if (!shownByApple) setShowEducation(true);
                }}
                className="w-full py-3 rounded-xl bg-primary-50 text-primary-700 font-semibold flex items-center justify-center gap-2"
              >
                <ContactlessIcon className="w-4 h-4" /> How to accept a tap
              </button>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" /> Done
              </button>
            </div>
          ) : !isAuthorized ? (
            <p className="text-sm text-slate-500 text-center px-2 py-3 bg-slate-50 rounded-xl">
              Setting up Tap to Pay includes accepting Apple's terms, so it
              needs someone with payment permissions — ask a manager to set
              it up, or to grant you payment access.
            </p>
          ) : (
            <button
              onClick={runSetup}
              disabled={state === 'connecting'}
              className="w-full py-3 rounded-xl bg-primary-600 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {state === 'connecting' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Setting up…
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" /> Set up Tap to Pay
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
